import React, { memo, useEffect, useState } from 'react';
import {
  WebScrollView,
  LoadingContainer,
  Space,
} from '@redlink/components/atoms';
import { PageList, ListContainer } from '@redlink/components/organisms';
import { createUseStyle } from '@redlink/assets/styles';
import {
  useQuery,
  getUserPagesQuery,
  useMutation,
  signInPageQuery,
  requestForRenewPageQuery,
} from '@redlink/services';
import { clearCache } from '@redlink/services/cache';
import { useLocale } from '@redlink/assets/locale';
import { NewPagePage, PurchasePagePage } from '@redlink/components/pages';
import { useValues } from '@redlink/assets/values';
import AsyncStorage from '@react-native-community/async-storage';
import { useTooltip } from '@redlink/components/molecules';

export const PagesPage = memo(({ navigation }) => {
  const { styles } = useStyle();
  const { dictionary } = useLocale();
  const { routes } = useValues();
  const [newPageIsVisible, setNewPageIsVisible] = useState(false);
  const [purchasePageIsVisible, setPurchasePageIsVisible] = useState(false);
  const [isChangingPage, setIsChangingPage] = useState(false);
  const { setNavigation } = useTooltip();

  useEffect(() => setNavigation(navigation), []);

  const {
    loading: getUserPagesIsBusy,
    data: { UserPages: { active = [], deactive = [] } = {} },
  } = useQuery(getUserPagesQuery, {
    failed: ({ code }) => {
      code === 4 && navigation.navigate(routes.registerWithPhone.key);
    },
  });

  const [signInPage, { loading: signInPageIsBusy }] = useMutation(
    signInPageQuery,
    {
      done: async ({ SignInPage: { token } }) => {
        setIsChangingPage(true);
        await clearCache();
        await AsyncStorage.setItem('@token', token);
        window.location = `${window.location.protocol}//${window.location.host}/${routes.dashboard.name}`;
      },
    }
  );

  const [requestForRenewPlan, { loading: requestForRenewPlanIsBusy }] =
    useMutation(requestForRenewPageQuery, {
      done: ({ RequestForRenewPage: { paymentUrl } }) =>
        (window.location.href = paymentUrl),
    });

  const hasArchive = deactive.length > 0;

  return (
    <LoadingContainer
      isBusy={
        getUserPagesIsBusy ||
        signInPageIsBusy ||
        isChangingPage ||
        requestForRenewPlanIsBusy
      }
      style={styles.container}
    >
      <WebScrollView>
        <ListContainer
          title={dictionary.pages}
          containerContainerStyle={styles.categoriesContainerStyle}
        >
          <PageList
            data={active}
            onSelect={({ id }) => {
              signInPage({ pageId: id });
            }}
            onAddPress={() => setNewPageIsVisible(true)}
          />
        </ListContainer>
        <Space />
        {hasArchive && (
          <ListContainer
            title={dictionary.deactivePages}
            description={dictionary.deactivePagesDescription}
            containerContainerStyle={styles.categoriesContainerStyle}
          >
            <PageList
              archive
              data={deactive}
              onSelect={({ id }) => {
                requestForRenewPlan({ pageId: id });
              }}
            />
          </ListContainer>
        )}
      </WebScrollView>
      <NewPagePage
        isVisible={newPageIsVisible}
        onDismiss={() => {
          setNewPageIsVisible(false);
        }}
      />
      <PurchasePagePage
        isVisible={purchasePageIsVisible}
        onDismiss={() => {
          setPurchasePageIsVisible(false);
        }}
      />
    </LoadingContainer>
  );
});

const useStyle = createUseStyle(({ colors, size }) => ({
  container: {
    flex: 1,
  },
  categoriesContainerStyle: {
    marginTop: 16,
  },
}));
